import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'lookupLists'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return {
		headers: {
			Authorization: `Bearer ${userInfo.token}`,
		},
	}
}

const initialState: {
	entities: Record<string, any>
	ids: string[]
	isLoading: boolean
	isInitialized: boolean
	error: string | null
} = {
	entities: {},
	ids: [],
	isLoading: false,
	isInitialized: false,
	error: null,
}

export const fetchLookupLists = createAsyncThunk(
	`${SLICE_NAME}/fetch`,
	async (listType: string | undefined, { getState, rejectWithValue }) => {
		try {
			const params = listType ? { params: { listType } } : {}
			const { data } = await axios.get('/api/lookup-lists', { ...params, ...prepConfig(getState) })
			return data
		} catch (error) {
			return rejectWithValue('Failed to fetch lookup lists')
		}
	},
)

export const createLookupList = createAsyncThunk(
	`${SLICE_NAME}/create`,
	async (payload: { listType: string; option: string; isPermanent?: boolean }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post('/api/lookup-lists', payload, prepConfig(getState))
			return data
		} catch (error: any) {
			return rejectWithValue(error?.response?.data?.message || 'Failed to create lookup list option')
		}
	},
)

export const updateLookupList = createAsyncThunk(
	`${SLICE_NAME}/update`,
	async (payload: { _id: string; option?: string; isPermanent?: boolean }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put(`/api/lookup-lists/${payload._id}`, payload, prepConfig(getState))
			return data
		} catch (error: any) {
			return rejectWithValue(error?.response?.data?.message || 'Failed to update lookup list option')
		}
	},
)

export const deleteLookupList = createAsyncThunk(
	`${SLICE_NAME}/delete`,
	async (id: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.delete(`/api/lookup-lists/${id}`, prepConfig(getState))
			return { ...data, _id: id }
		} catch (error: any) {
			return rejectWithValue(error?.response?.data?.message || 'Failed to delete lookup list option')
		}
	},
)

export const seedLookupLists = createAsyncThunk(
	`${SLICE_NAME}/seed`,
	async (_, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post('/api/lookup-lists/seed', {}, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to seed lookup lists')
		}
	},
)

const lookupListSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: {
		[fetchLookupLists.pending as any]: (state: any) => {
			state.isLoading = true
		},
		[fetchLookupLists.fulfilled as any]: (state: any, action: any) => {
			const items = Array.isArray(action.payload) ? action.payload : []
			state.entities = {}
			state.ids = []
			items.forEach((item: any) => {
				state.entities[item._id] = item
				state.ids.push(item._id)
			})
			state.isLoading = false
			state.isInitialized = true
		},
		[fetchLookupLists.rejected as any]: (state: any, action: any) => {
			state.isLoading = false
			state.error = action.payload
		},
		[createLookupList.fulfilled as any]: (state: any, action: any) => {
			const item = action.payload
			state.entities[item._id] = item
			if (!state.ids.includes(item._id)) state.ids.push(item._id)
		},
		[updateLookupList.fulfilled as any]: (state: any, action: any) => {
			const item = action.payload
			state.entities[item._id] = item
		},
		[deleteLookupList.fulfilled as any]: (state: any, action: any) => {
			const id = action.payload._id
			delete state.entities[id]
			state.ids = state.ids.filter((i: string) => i !== id)
		},
	} as any,
})

export default lookupListSlice
